import React, { FC } from "react";
import { View, Text } from "@react-pdf/renderer";

import { styles } from "../PaygapReportPdf.styled";

interface ExecutiveSummaryProps {
  title: string;
  aiGeneratedText?: string;
  fallbackText: string;
  keyFindings?: string[];
  keyFindingsTitle?: string;
  regressionParametersLabel?: string;
  regressionParametersValue?: string;
}

const ExecutiveSummary: FC<ExecutiveSummaryProps> = ({
  title,
  aiGeneratedText,
  fallbackText,
  keyFindings,
  keyFindingsTitle,
  regressionParametersLabel,
  regressionParametersValue,
}) => {
  const summaryText = aiGeneratedText || fallbackText;

  // Split text into paragraphs
  const paragraphs = summaryText.split("\n\n").filter((p) => p.trim());

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>

      {paragraphs.map((paragraph, index) => (
        <Text key={index} style={styles.paragraph}>
          {paragraph.trim()}
        </Text>
      ))}

      {keyFindings && keyFindings.length > 0 && (
        <View style={styles.mt16}>
          {keyFindingsTitle && (
            <Text style={styles.sectionSubtitle}>{keyFindingsTitle}</Text>
          )}
          {keyFindings.map((finding, index) => (
            <View
              key={index}
              style={{
                flexDirection: "row",
                marginBottom: 6,
                paddingLeft: 10,
              }}
            >
              <Text style={styles.text}>• </Text>
              <Text style={[styles.text, { flex: 1 }]}>{finding}</Text>
            </View>
          ))}
        </View>
      )}

      {regressionParametersLabel && regressionParametersValue && (
        <View
          style={{
            marginTop: 14,
            padding: 10,
            backgroundColor: "#e3f2fd",
            borderRadius: 4,
            borderLeftWidth: 3,
            borderLeftColor: "#1976d2",
          }}
        >
          <Text style={styles.text}>
            <Text style={styles.textBold}>{regressionParametersLabel} </Text>
            {regressionParametersValue}
          </Text>
        </View>
      )}
    </View>
  );
};

export default ExecutiveSummary;

/**
 * Generate fallback summary text based on analysis data
 */
export const generateFallbackSummary = (
  genderPayGap: number,
  employeeCount: number,
  complianceRisk: string,
  isSignificant: boolean,
  language: string,
  t?: (key: string, defaultValue: string, options?: Record<string, unknown>) => string,
): string => {
  if (t) {
    const significanceText = isSignificant
      ? t("paygap.report.fallback.significant", "statistically significant")
      : t("paygap.report.fallback.notSignificant", "not statistically significant");

    const riskText = complianceRisk === "High"
      ? t("paygap.report.high", "High")
      : complianceRisk === "Medium"
        ? t("paygap.report.medium", "Medium")
        : t("paygap.report.low", "Low");

    const gapDirection = genderPayGap === 0
      ? t("paygap.report.fallback.noGap", "The organization demonstrates excellent pay equity practices with no detectable pay gap between genders.")
      : genderPayGap > 0
        ? t("paygap.report.fallback.menEarnMore", "The data indicates that on average, male employees earn more than female employees when controlling for relevant factors.")
        : t("paygap.report.fallback.womenEarnMore", "The data indicates that on average, female employees earn more than male employees when controlling for relevant factors.");

    const intro = t("paygap.report.fallback.intro", "This report presents a comprehensive analysis of gender pay equity within the organization based on {{count}} employee records.", { count: employeeCount });
    const analysis = t("paygap.report.fallback.analysis", "The analysis reveals a gender pay gap of {{gap}}%, which is {{significance}}. The overall compliance risk has been assessed as {{risk}}.", { gap: genderPayGap.toFixed(1), significance: significanceText, risk: riskText.toLowerCase() });
    const methodology = t("paygap.report.fallback.methodology", "This analysis takes into account various factors including job position, seniority level, education, and work experience to provide an adjusted view of pay equity within the organization.");

    return `${intro}\n\n${analysis}\n\n${gapDirection}\n\n${methodology}`;
  }

  // Legacy fallback without t() — kept for backward compatibility
  const summaries: Record<string, string> = {
    en: `This report presents a comprehensive analysis of gender pay equity within the organization based on ${employeeCount} employee records.\n\nThe analysis reveals a gender pay gap of ${genderPayGap.toFixed(1)}%, which is ${isSignificant ? "statistically significant" : "not statistically significant"}. The overall compliance risk has been assessed as ${complianceRisk.toLowerCase()}.\n\n${genderPayGap === 0 ? "The organization demonstrates excellent pay equity practices with no detectable pay gap between genders." : genderPayGap > 0 ? "The data indicates that on average, male employees earn more than female employees when controlling for relevant factors." : "The data indicates that on average, female employees earn more than male employees when controlling for relevant factors."}\n\nThis analysis takes into account various factors including job position, seniority level, education, and work experience to provide an adjusted view of pay equity within the organization.`,
    cs: `Tato zpráva představuje komplexní analýzu genderové rovnosti v odměňování v organizaci na základě ${employeeCount} záznamů zaměstnanců.\n\nAnalýza odhaluje genderový rozdíl v odměňování ve výši ${genderPayGap.toFixed(1)}%, což je ${isSignificant ? "statisticky významné" : "statisticky nevýznamné"}. Celkové riziko souladu s předpisy bylo vyhodnoceno jako ${complianceRisk === "High" ? "vysoké" : complianceRisk === "Medium" ? "střední" : "nízké"}.\n\n${genderPayGap === 0 ? "Organizace vykazuje vynikající postupy v oblasti rovnosti odměňování bez zjistitelného rozdílu v odměňování mezi pohlavími." : genderPayGap > 0 ? "Data naznačují, že v průměru muži vydělávají více než ženy při zohlednění relevantních faktorů." : "Data naznačují, že v průměru ženy vydělávají více než muži při zohlednění relevantních faktorů."}\n\nTato analýza zohledňuje různé faktory včetně pracovní pozice, úrovně seniority, vzdělání a pracovních zkušeností, aby poskytla upravený pohled na rovnost odměňování v organizaci.`,
    sk: `Táto správa predstavuje komplexnú analýzu rodovej rovnosti v odmeňovaní v organizácii na základe ${employeeCount} záznamov zamestnancov.\n\nAnalýza odhaľuje rodový rozdiel v odmeňovaní vo výške ${genderPayGap.toFixed(1)}%, čo je ${isSignificant ? "štatisticky významné" : "štatisticky nevýznamné"}. Celkové riziko súladu s predpismi bolo vyhodnotené ako ${complianceRisk === "High" ? "vysoké" : complianceRisk === "Medium" ? "stredné" : "nízke"}.\n\n${genderPayGap === 0 ? "Organizácia vykazuje vynikajúce postupy v oblasti rovnosti odmeňovania bez zistiteľného rozdielu v odmeňovaní medzi pohlaviami." : genderPayGap > 0 ? "Údaje naznačujú, že v priemere muži zarábajú viac ako ženy pri zohľadnení relevantných faktorov." : "Údaje naznačujú, že v priemere ženy zarábajú viac ako muži pri zohľadnení relevantných faktorov."}\n\nTáto analýza zohľadňuje rôzne faktory vrátane pracovnej pozície, úrovne seniority, vzdelania a pracovných skúseností, aby poskytla upravený pohľad na rovnosť odmeňovania v organizácii.`,
  };

  return summaries[language] || summaries.en;
};

/**
 * Generate key findings based on analysis data
 */
export const generateKeyFindings = (
  genderPayGap: number,
  unadjustedPayGap: number,
  employeeCount: number,
  menCount: number,
  womenCount: number,
  complianceRisk: string,
  language: string,
  t?: (key: string, defaultValue: string, options?: Record<string, unknown>) => string,
): string[] => {
  if (t) {
    const riskText = complianceRisk === "High"
      ? t("paygap.report.high", "High")
      : complianceRisk === "Medium"
        ? t("paygap.report.medium", "Medium")
        : t("paygap.report.low", "Low");

    const womenPercent = ((womenCount / employeeCount) * 100).toFixed(1);
    const menPercent = ((menCount / employeeCount) * 100).toFixed(1);

    return [
      t("paygap.report.findings.adjustedGap", "Adjusted gender pay gap: {{gap}}%", { gap: genderPayGap.toFixed(1) }),
      t("paygap.report.findings.unadjustedGap", "Unadjusted gender pay gap: {{gap}}%", { gap: unadjustedPayGap.toFixed(1) }),
      t("paygap.report.findings.totalEmployees", "Total employees analyzed: {{count}} ({{men}} men, {{women}} women)", { count: employeeCount, men: menCount, women: womenCount }),
      t("paygap.report.findings.composition", "Workforce composition: {{womenPct}}% women, {{menPct}}% men", { womenPct: womenPercent, menPct: menPercent }),
      t("paygap.report.findings.riskLevel", "Compliance risk level: {{risk}}", { risk: riskText }),
    ];
  }

  // Legacy fallback without t()
  const findings: Record<string, string[]> = {
    en: [
      `Adjusted gender pay gap: ${genderPayGap.toFixed(1)}%`,
      `Unadjusted gender pay gap: ${unadjustedPayGap.toFixed(1)}%`,
      `Total employees analyzed: ${employeeCount} (${menCount} men, ${womenCount} women)`,
      `Workforce composition: ${((womenCount / employeeCount) * 100).toFixed(1)}% women, ${((menCount / employeeCount) * 100).toFixed(1)}% men`,
      `Compliance risk level: ${complianceRisk}`,
    ],
    cs: [
      `Upravený genderový rozdíl: ${genderPayGap.toFixed(1)}%`,
      `Neupravený genderový rozdíl: ${unadjustedPayGap.toFixed(1)}%`,
      `Celkem analyzovaných zaměstnanců: ${employeeCount} (${menCount} mužů, ${womenCount} žen)`,
      `Složení pracovní síly: ${((womenCount / employeeCount) * 100).toFixed(1)}% žen, ${((menCount / employeeCount) * 100).toFixed(1)}% mužů`,
      `Úroveň rizika souladu: ${complianceRisk === "High" ? "Vysoké" : complianceRisk === "Medium" ? "Střední" : "Nízké"}`,
    ],
    sk: [
      `Upravený rodový rozdiel: ${genderPayGap.toFixed(1)}%`,
      `Neupravený rodový rozdiel: ${unadjustedPayGap.toFixed(1)}%`,
      `Celkom analyzovaných zamestnancov: ${employeeCount} (${menCount} mužov, ${womenCount} žien)`,
      `Zloženie pracovnej sily: ${((womenCount / employeeCount) * 100).toFixed(1)}% žien, ${((menCount / employeeCount) * 100).toFixed(1)}% mužov`,
      `Úroveň rizika súladu: ${complianceRisk === "High" ? "Vysoké" : complianceRisk === "Medium" ? "Stredné" : "Nízke"}`,
    ],
  };

  return findings[language] || findings.en;
};
